<template>
  <div class="model-research-page" :class="`layout-${currentLayout}`">
    <v-app-bar
      elevation="0"
      class="controls-bar"
      :color="isDark ? 'grey-darken-4' : 'white'"
    >
      <v-container>
        <v-row align="center" no-gutters class="pa-2">
          <v-col cols="auto" class="flex-grow-1">
            <v-btn-toggle
              v-model="currentLayout"
              mandatory
              density="compact"
              variant="outlined"
              divided
              class="layout-toggle"
            >
              <v-btn value="classic" size="small">
                <v-icon>mdi-view-list</v-icon>
                <span class="d-none d-sm-inline ml-1">Classic</span>
              </v-btn>
              <v-btn value="minimal" size="small">
                <v-icon>mdi-view-headline</v-icon>
                <span class="d-none d-sm-inline ml-1">Minimal</span>
              </v-btn>
              <v-btn value="carded" size="small">
                <v-icon>mdi-view-grid</v-icon>
                <span class="d-none d-sm-inline ml-1">Carded</span>
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <v-col cols="auto">
            <v-tooltip
              :text="isDark ? 'Light Mode' : 'Dark Mode'"
              location="bottom"
            >
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  :icon="isDark ? 'mdi-weather-sunny' : 'mdi-weather-night'"
                  @click="toggleTheme"
                  variant="text"
                  size="small"
                />
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <v-main class="main-content">
      <v-container
        v-if="loading"
        class="d-flex justify-center align-center"
        style="min-height: 80vh"
      >
        <v-progress-circular
          :size="100"
          :width="8"
          indeterminate
          color="primary"
        />
      </v-container>

      <v-container v-else-if="error" class="text-center">
        <v-alert type="error" class="mt-10">
          <h3>Error Loading Model Data</h3>
          <p>{{ error }}</p>
        </v-alert>
      </v-container>

      <template v-else-if="modelData">
        <div v-if="currentLayout === 'classic'" class="layout-classic">
          <ClassicLayout
            :modelData="modelData"
            :selectedTrim="selectedTrim"
            :selectedColor="selectedColor"
            :comparisonTrims="comparisonTrims"
            :availableColors="availableColors"
            :lowestPrice="lowestPrice"
            :currentVehicleImage="currentVehicleImage"
            :showTrimComparing="showTrimComparing"
            :showModelComparing="showModelComparing"
            @select-trim="selectTrim"
            @select-color="selectColor"
            @add-to-comparison="addToComparison"
            @remove-from-comparison="removeTrimFromComparison"
            @clear-comparison="clearComparison"
            @diff-toggle="onDiffToggle"
          >
            <template #model-comparison>
              <CrossModelComparison
                v-if="showModelComparing && modelData && selectedTrim"
                :currentModel="modelData"
                :currentTrim="selectedTrim"
                :selectedYear="comparisonYear"
                :selectedMake="comparisonMake"
                :selectedModel="comparisonModel"
                @update:selectedYear="updateComparisonYear"
                @update:selectedMake="updateComparisonMake"
                @update:selectedModel="updateComparisonModel"
              />
            </template>
          </ClassicLayout>
        </div>

        <div v-else-if="currentLayout === 'minimal'" class="layout-minimal">
          <MinimalLayout
            :modelData="modelData"
            :selectedTrim="selectedTrim"
            :selectedColor="selectedColor"
            :comparisonTrims="comparisonTrims"
            :availableColors="availableColors"
            :lowestPrice="lowestPrice"
            :currentVehicleImage="currentVehicleImage"
            :showTrimComparing="showTrimComparing"
            :showModelComparing="showModelComparing"
            @select-trim="selectTrim"
            @select-color="selectColor"
            @add-to-comparison="addToComparison"
            @remove-from-comparison="removeTrimFromComparison"
            @clear-comparison="clearComparison"
            @diff-toggle="onDiffToggle"
          >
            <template #model-comparison>
              <CrossModelComparison
                v-if="showModelComparing && modelData && selectedTrim"
                :currentModel="modelData"
                :currentTrim="selectedTrim"
                :selectedYear="comparisonYear"
                :selectedMake="comparisonMake"
                :selectedModel="comparisonModel"
                @update:selectedYear="updateComparisonYear"
                @update:selectedMake="updateComparisonMake"
                @update:selectedModel="updateComparisonModel"
              />
            </template>
          </MinimalLayout>
        </div>

        <div v-else-if="currentLayout === 'carded'" class="layout-carded">
          <CardedLayout
            :modelData="modelData"
            :selectedTrim="selectedTrim"
            :selectedColor="selectedColor"
            :comparisonTrims="comparisonTrims"
            :availableColors="availableColors"
            :lowestPrice="lowestPrice"
            :currentVehicleImage="currentVehicleImage"
            :showTrimComparing="showTrimComparing"
            :showModelComparing="showModelComparing"
            @select-trim="selectTrim"
            @select-color="selectColor"
            @add-to-comparison="addToComparison"
            @remove-from-comparison="removeTrimFromComparison"
            @clear-comparison="clearComparison"
            @diff-toggle="onDiffToggle"
          >
            <template #model-comparison>
              <CrossModelComparison
                v-if="showModelComparing && modelData && selectedTrim"
                :currentModel="modelData"
                :currentTrim="selectedTrim"
                :selectedYear="comparisonYear"
                :selectedMake="comparisonMake"
                :selectedModel="comparisonModel"
                @update:selectedYear="updateComparisonYear"
                @update:selectedMake="updateComparisonMake"
                @update:selectedModel="updateComparisonModel"
              />
            </template>
          </CardedLayout>
        </div>
      </template>
    </v-main>

    <v-speed-dial
      v-if="showTrimComparing && comparisonTrims.length > 0"
      v-model="fabOpen"
      location="bottom end"
      transition="fade-transition"
      class="d-md-none"
    >
      <template #activator="{ props: activatorProps }">
        <v-badge :content="comparisonTrims.length" color="error" floating>
          <v-btn
            v-bind="activatorProps"
            color="primary"
            icon="mdi-compare"
            size="large"
          />
        </v-badge>
      </template>
      <v-btn
        key="1"
        color="success"
        icon="mdi-eye"
        size="small"
        @click="showComparisonDialog = true"
      />
      <v-btn
        key="2"
        color="error"
        icon="mdi-delete"
        size="small"
        @click="clearComparison"
      />
    </v-speed-dial>

    <v-dialog
      v-if="showTrimComparing"
      v-model="showComparisonDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar color="primary" dark>
          <v-btn icon @click="showComparisonDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Trim Comparison</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-0">
          <EnhancedTrimComparison
            v-if="comparisonTrims.length > 1"
            :trims="comparisonTrims"
            :modelData="modelData || undefined"
            @remove-trim="removeTrimFromComparison"
            @clear-comparison="clearComparison"
            @diff-toggle="onDiffToggle"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from 'vuetify'
import { useSettingsStore } from '@/store/settings'
import { useModelComparisonStore } from '@/store/modelComparison'
import ClassicLayout from '@/components/layouts/ClassicLayout.vue'
import MinimalLayout from '@/components/layouts/MinimalLayout.vue'
import CardedLayout from '@/components/layouts/CardedLayout.vue'
import EnhancedTrimComparison from '@/components/EnhancedTrimComparison.vue'
import CrossModelComparison from '@/components/CrossModelComparison.vue'
import { fetchDataHubModel } from '@/apis'
import { getCDNUrl } from '@/utils'
import { tracker } from '@/services/tracking'
import type { ModelData, TrimData, ColorOption, ImageData } from '@/types'

const route = useRoute()
const theme = useTheme()
const settingsStore = useSettingsStore()
const comparisonStore = useModelComparisonStore()
const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const loading = ref(true)
const error = ref<string | null>(null)
const modelData = ref<ModelData | null>(null)
const selectedTrim = ref<TrimData | null>(null)
const selectedColor = ref<ColorOption | null>(null)
const comparisonTrims = ref<TrimData[]>([])

const currentLayout = ref<'classic' | 'minimal' | 'carded'>('classic')
const isDark = ref(false)
const fabOpen = ref(false)
const showComparisonDialog = ref(false)

const comparisonYear = computed({
  get: () => comparisonStore.selectedYear,
  set: value =>
    comparisonStore.setComparisonSelection(
      value,
      comparisonMake.value,
      comparisonModel.value,
    ),
})
const comparisonMake = computed({
  get: () => comparisonStore.selectedMake,
  set: value =>
    comparisonStore.setComparisonSelection(
      comparisonYear.value,
      value,
      comparisonModel.value,
    ),
})
const comparisonModel = computed({
  get: () => comparisonStore.selectedModel,
  set: value =>
    comparisonStore.setComparisonSelection(
      comparisonYear.value,
      comparisonMake.value,
      value,
    ),
})

const showTrimComparing = computed(() => {
  return settingsStore.settings?.showTrimComparing ?? true
})

const showModelComparing = computed(() => {
  return settingsStore.settings?.showModelComparing ?? true
})

onMounted(async () => {
  await settingsStore.fetchSettings()

  await fetchModelData()

  const { year, make, model } = route.params
  tracker.trackPageView('model_research', {
    year: String(year),
    make: String(make),
    model: String(model),
    layout: currentLayout.value,
    theme: isDark.value ? 'dark' : 'light',
  })

  const savedLayout = localStorage.getItem('modelResearchLayout')
  const savedTheme = localStorage.getItem('modelResearchTheme')

  if (savedLayout && ['classic', 'minimal', 'carded'].includes(savedLayout)) {
    currentLayout.value = savedLayout as 'classic' | 'minimal' | 'carded'
  }

  if (savedTheme) {
    isDark.value = savedTheme === 'dark'
    theme.global.name.value = savedTheme === 'dark' ? 'dark' : 'light'
  }
})

watch(currentLayout, (newLayout, oldLayout) => {
  localStorage.setItem('modelResearchLayout', newLayout)
  tracker.trackDesignSwitch(oldLayout, newLayout, {
    year: String(route.params.year),
    make: String(route.params.make),
    model: String(route.params.model),
  })
})

const lowestPrice = computed(() => {
  if (!modelData.value?.trims?.length) return 0
  const prices = modelData.value.trims
    .map((t: TrimData) => Number(t.baseMsrp || t.price || 0))
    .filter((p: number) => p > 0)
  return prices.length ? Math.min(...prices) : 0
})

const availableColors = computed((): ColorOption[] => {
  if (!selectedTrim.value) {
    const colorMap = new Map<string, ColorOption>()
    modelData.value?.trims?.forEach((trim: TrimData) => {
      trim.colors?.forEach((color: ColorOption) => {
        if (!colorMap.has(color.hex)) {
          colorMap.set(color.hex, color)
        }
      })
    })
    return Array.from(colorMap.values())
  }
  return selectedTrim.value.colors || []
})

const currentVehicleImage = computed(() => {
  if (selectedColor?.value && selectedTrim.value) {
    const colorImage = selectedTrim.value.images?.colorized?.find(
      (img: ImageData) => img.colorHex === selectedColor.value?.hex,
    )
    if (colorImage) return colorImage.url
  }
  return (
    selectedTrim.value?.img ||
    modelData.value?.trims?.[0]?.img ||
    noVehicleImage
  )
})

const fetchModelData = async () => {
  try {
    loading.value = true
    error.value = null

    const { year, make, model } = route.params
    const response = await fetchDataHubModel(
      Number(year),
      String(make),
      String(model),
    )

    if (response) {
      modelData.value = response
      if (response.trims?.length > 0) {
        selectedTrim.value = response.trims[0]
      }
    } else {
      throw new Error('No data available for this model')
    }
  } catch (err) {
    console.error('Error fetching model data:', err)
    error.value =
      err instanceof Error ? err.message : 'Failed to load model data'
  } finally {
    loading.value = false
  }
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  theme.global.name.value = isDark.value ? 'dark' : 'light'
  localStorage.setItem('modelResearchTheme', isDark.value ? 'dark' : 'light')
  tracker.trackThemeChange(isDark.value ? 'dark' : 'light')
}

const selectTrim = (trim: TrimData) => {
  selectedTrim.value = trim
  selectedColor.value = null
  tracker.trackTrimSelect(trim.id, trim.trim || trim.styleName || '')
}

const selectColor = (color: ColorOption) => {
  selectedColor.value = color
}

const addToComparison = (trim: TrimData) => {
  const existingIndex = comparisonTrims.value.findIndex(t => t.id === trim.id)

  if (existingIndex !== -1) {
    comparisonTrims.value.splice(existingIndex, 1)
    tracker.trackCompareAction('remove', [trim.id])
  } else {
    if (comparisonTrims.value.length < 4) {
      comparisonTrims.value.push(trim)
      tracker.trackCompareAction(
        'add',
        comparisonTrims.value.map(t => t.id),
      )
    } else {
      alert(
        'Maximum 4 trims can be compared at once. Please remove a trim first.',
      )
    }
  }
}

const removeTrimFromComparison = (trimId: string) => {
  comparisonTrims.value = comparisonTrims.value.filter(t => t.id !== trimId)
  tracker.trackCompareAction(
    'remove',
    comparisonTrims.value.map(t => t.id),
  )
}

const clearComparison = () => {
  const previousTrims = comparisonTrims.value.map(t => t.id)
  comparisonTrims.value = []
  tracker.trackCompareAction('clear', previousTrims)
}

const onDiffToggle = (enabled: boolean) => {
  console.log('Diff toggle:', enabled)
}

const updateComparisonYear = (year: number | null) => {
  comparisonYear.value = year
  comparisonStore.setComparisonSelection(
    year,
    comparisonMake.value,
    comparisonModel.value,
  )
}

const updateComparisonMake = (make: string | null) => {
  comparisonMake.value = make
  comparisonStore.setComparisonSelection(
    comparisonYear.value,
    make,
    comparisonModel.value,
  )
}

const updateComparisonModel = (model: string | null) => {
  comparisonModel.value = model
  comparisonStore.setComparisonSelection(
    comparisonYear.value,
    comparisonMake.value,
    model,
  )
}
</script>

<style scoped>
.model-research-page {
  min-height: 100vh;
  transition: background-color 0.3s ease;
  background-color: #ffffff;
}

.v-theme--dark .model-research-page {
  background-color: #121212;
}

.controls-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.main-content {
  padding-top: 0;
  background-color: #ffffff;
}

.v-theme--dark .main-content {
  background-color: #121212;
}

.v-theme--dark .v-main {
  background-color: #121212 !important;
}

.layout-classic {
}

.layout-minimal {
}

.layout-carded {
}

.v-theme--dark .controls-bar {
  border-bottom-color: rgba(255, 255, 255, 0.12);
  background-color: #1e1e1e !important;
}

.v-theme--dark .v-app-bar {
  background-color: #1e1e1e !important;
}

.v-theme--dark .v-container {
  background-color: transparent !important;
}

.v-theme--dark .v-row {
  background-color: transparent !important;
}

.v-theme--dark .v-col {
  background-color: transparent !important;
}

.v-theme--dark .v-card {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}

.v-theme--dark .v-card-text {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}

.v-theme--dark .v-sheet {
  background-color: #1e1e1e !important;
}

.v-theme--dark .v-alert {
  background-color: #2c2c2c !important;
  color: #e0e0e0 !important;
}

.v-theme--dark section {
  background-color: transparent !important;
}

.v-theme--dark .stats-section {
  background-color: #1e1e1e !important;
}

.v-theme--dark .trims-section {
  background-color: #121212 !important;
}

.v-theme--dark .color-section {
  background-color: #1e1e1e !important;
}

.v-theme--dark .details-section {
  background-color: #121212 !important;
}

.v-theme--dark .comparison-section {
  background-color: #1e1e1e !important;
}

@media (max-width: 960px) {
  .controls-bar :deep(.v-toolbar__content) {
    padding: 8px;
  }
}

*:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.skip-nav {
  position: absolute;
  top: -40px;
  left: 0;
  background: rgb(var(--v-theme-primary));
  color: white;
  padding: 8px;
  text-decoration: none;
  z-index: 200;
}

.skip-nav:focus {
  top: 0;
}

@media print {
  .controls-bar {
    display: none;
  }

  .v-speed-dial {
    display: none !important;
  }

  .model-research-page {
    background: white !important;
  }
}
</style>
